import type { AttributeBlacklistConfig, BasicViewItem } from 'configuration-schemas/viewdefns/general';
import { pick, values } from 'lodash';
import { BasicItem } from 'src/worker/pivotWorker.types';

type HasDataIndex = Pick<BasicViewItem, 'dataIndex'>;
type HasId = Pick<BasicItem, 'id'>;
export const AttributeBlacklistDefnId = 'AttributeBlacklist';

/**
 * Collects every attribute blacklisted for a given department by walking
 * the configured blacklist groups and unioning the attributeBlacklist arrays
 * of groups whose scopeProducts include `currentDepartment`.
 */
const collectAndExpandBlacklistedAttributes = (
  config: AttributeBlacklistConfig,
  currentDepartment: string,
): string[] => {
  return (
    Object.values(config.attributeBlacklistGroups)
      .filter((group) => group.scopeProducts.includes(currentDepartment))
      .flatMap((group) => group.attributeBlacklist)
      /** This specifically accounts for alternate keys which are used many places */
      .flatMap((blk) => [blk, `attribute:${blk}:name`, `attribute:${blk}:id`])
  );
};

const notBlacklistedByKey = (obj: HasDataIndex | HasId, key: string, blackList: string[]): boolean => {
  const keyedValues = values(pick(obj, [key])) as string[];
  return !blackList.includes(keyedValues[0]);
};

export const isNotBlacklistedByDataIndex = (config: AttributeBlacklistConfig, currentDepartment: string) => {
  const blacklist = collectAndExpandBlacklistedAttributes(config, currentDepartment);
  return (t: BasicViewItem): boolean => notBlacklistedByKey(t, 'dataIndex', blacklist);
};

export const isNotBlacklistedById = (config: AttributeBlacklistConfig, currentDepartment: string) => {
  const blacklisted = collectAndExpandBlacklistedAttributes(config, currentDepartment);
  return (t: HasId): boolean => notBlacklistedByKey(t, 'id', blacklisted);
};

export const blacklistByDataIndex = <T extends HasDataIndex>(
  obj: T[],
  config: AttributeBlacklistConfig,
  currentDepartment: string,
): T[] => obj.filter(isNotBlacklistedByDataIndex(config, currentDepartment));

type NestedByDataIndex = HasDataIndex & {
  view?: NestedByDataIndex[];
  columns?: NestedByDataIndex[];
};

export const blacklistByDataIndexDeep = <T extends NestedByDataIndex>(
  obj: T[],
  config: AttributeBlacklistConfig,
  currentDepartment: string,
): T[] => {
  const isNotCurrentBlacklistByDataIndex = isNotBlacklistedByDataIndex(config, currentDepartment);
  const walk = (list: T[]): T[] =>
    list.filter(isNotCurrentBlacklistByDataIndex).map((item) => ({
      ...item,
      ...(item.view ? { view: walk(item.view as T[]) } : {}),
      ...(item.columns ? { columns: walk(item.columns as T[]) } : {}),
    }));
  return walk(obj);
};

export const blacklistById = <T extends HasId>(
  obj: T[],
  config: AttributeBlacklistConfig,
  currentDepartment: string,
): T[] => obj.filter(isNotBlacklistedById(config, currentDepartment));
